import type { PaymentRecord } from '@/pages/Payment/paymentStore'
import { api } from '@/services/api'

const inr = (n: number) =>
  `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

/** Resolve the Base / GST / TDS / Total-Received breakdown, with graceful
 *  fallbacks for older records that only stored `amount` + `tds`. */
export const receiptBreakdown = (r: PaymentRecord) => {
  const gross = Number(r.amount) || 0
  const gst   = Number(r.gstAmount ?? 0)
  const base  = Number(r.baseAmount ?? (gross - gst))
  const tds   = Number(r.tds) || 0
  const received = Number(r.amountReceived ?? (gross - tds))
  return { gross, base, gst, tds, received }
}

export const generateReceiptHTML = (r: PaymentRecord): string => {
  const { base, gst, tds, received } = receiptBreakdown(r)
  return `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Receipt - ${r.receiptNumber}</title>
  <style>
    *{box-sizing:border-box}
    body{font-family:Arial,sans-serif;padding:30px;color:#111;background:#fff}
    .head{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #001A4D;padding-bottom:10px;margin-bottom:18px}
    h2{color:#001A4D;margin:0}
    .sub{font-size:11px;color:#8A93A6;margin-top:2px}
    table{width:100%;border-collapse:collapse;font-size:13px;margin-top:8px}
    td{padding:9px 14px;border:1px solid #e3e6ee}
    td:first-child{font-weight:bold;background:#f4f6fa;width:42%}
    .amounts td:last-child{font-family:'JetBrains Mono',monospace;text-align:right}
    .total td{background:#EFFaf3;color:#065F46;font-size:16px;font-weight:bold}
    .inv-list{font-size:12px;color:#444}
    .footer{margin-top:36px;font-size:11px;color:#888;text-align:center;border-top:1px solid #ddd;padding-top:12px}
    @media print{body{padding:0}}
  </style></head><body>
  <div class="head">
    <div>
      <h2>Payment Receipt</h2>
      <div class="sub">Shakthi Power · GST-compliant collection receipt</div>
    </div>
    <div style="text-align:right">
      <div style="font-family:'JetBrains Mono',monospace;font-weight:bold;font-size:15px;color:#001A4D">${r.receiptNumber}</div>
      <div class="sub">${r.date}</div>
    </div>
  </div>

  <table>
    <tr><td>Received From</td><td>${r.customer} <span style="color:#8A93A6;font-size:12px">(${r.customerId})</span></td></tr>
    <tr><td>Invoices Settled</td><td class="inv-list">${(r.invoices || []).map(i => i.invoiceNumber).join(', ') || '—'}</td></tr>
    <tr><td>Payment Mode</td><td>${r.mode}</td></tr>
  </table>

  <table class="amounts" style="margin-top:14px">
    <tr><td>Base Amount</td><td>${inr(base)}</td></tr>
    <tr><td>GST Amount</td><td>${inr(gst)}</td></tr>
    <tr><td>TDS Deducted</td><td>${tds > 0 ? '– ' : ''}${inr(tds)}</td></tr>
    <tr class="total"><td>Total Amount Received</td><td>${inr(received)}</td></tr>
  </table>

  <div class="footer">Shakthi Power ERP · This is a computer-generated receipt · Generated on ${new Date().toLocaleDateString('en-IN')}</div>
  </body></html>`
}

/** Open a print dialog for the receipt. */
export const printReceipt = (r: PaymentRecord): void => {
  const html = generateReceiptHTML(r)
  const w = window.open('', '_blank')
  if (!w) return
  w.document.write(html + '<script>window.onload=function(){window.print()}<\/script>')
  w.document.close()
}

/**
 * Direct PDF download, rendered server-side via Puppeteer (real headless
 * Chromium). html2pdf.js/html2canvas (the previous approach) came back
 * blank in practice — same issue already fixed for Proposals/Agreements.
 */
export const downloadReceiptAsPDF = async (r: PaymentRecord): Promise<void> => {
  await api.downloadPaymentPdf(r.id, generateReceiptHTML(r), `${r.receiptNumber || r.id}.pdf`)
}

/**
 * Emails the receipt to the paying consumer (resolved server-side from the
 * payment's customerId) with the real PDF attached — rendered the same way
 * as the Download button. Previously this only opened a mailto: link with
 * no backend call and nothing ever actually sent.
 */
export const emailReceipt = async (r: PaymentRecord): Promise<string> => {
  const { base, gst, tds, received } = receiptBreakdown(r)
  const subject = `Payment Receipt ${r.receiptNumber} — Shakthi Power`
  const body = [
    `Dear ${r.customer},`,
    ``,
    `Thank you for your payment. Please find your receipt details below:`,
    ``,
    `Receipt Number : ${r.receiptNumber}`,
    `Date           : ${r.date}`,
    `Invoices       : ${(r.invoices || []).map(i => i.invoiceNumber).join(', ') || '—'}`,
    `Payment Mode   : ${r.mode}`,
    ``,
    `Base Amount           : ${inr(base)}`,
    `GST Amount            : ${inr(gst)}`,
    `TDS Deducted          : ${inr(tds)}`,
    `Total Amount Received : ${inr(received)}`,
    ``,
    `Regards,`,
    `Shakthi Power — Accounts Team`,
  ].join('\n')
  const result = await api.emailPayment(r.id, { subject, body, htmlContent: generateReceiptHTML(r) })
  return result.sentTo
}
